import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import styles from "./Card.module.css";

export type CardDepth = "base" | "raised" | "sunken";

export type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
  depth?: CardDepth;
  glitch?: boolean;
  overlay?: React.ReactNode;
};

const depthShadowClasses: Record<CardDepth, string> = {
  base: "shadow-neo-soft",
  raised:
    "shadow-neo hover:shadow-neo-strong focus-visible:shadow-neo-strong focus-within:shadow-neo-strong",
  sunken: "shadow-neo-inset",
};

const hasCardOverlay = (node: React.ReactNode): boolean => {
  if (node === null || node === undefined || typeof node === "boolean") {
    return false;
  }
  if (Array.isArray(node)) {
    return node.some((child) => hasCardOverlay(child));
  }
  if (React.isValidElement(node)) {
    type OverlayElementProps = {
      children?: React.ReactNode;
      ["data-card-overlay"]?: string;
    };
    const element = node as React.ReactElement<OverlayElementProps>;
    if (element.props?.["data-card-overlay"] === "true") {
      return true;
    }
    return hasCardOverlay(element.props?.children);
  }
  return false;
};

const Card = React.forwardRef<React.ElementRef<"div">, CardProps>(
  (
    {
      className,
      asChild = false,
      depth = "base",
      glitch = false,
      children,
      overlay,
      ...props
    },
    ref,
  ) => {
    const { ["data-text"]: providedGlitchText, ...restProps } = props as CardProps & {
      ["data-text"]?: unknown;
    };
    const glitchText =
      typeof providedGlitchText === "string" ? providedGlitchText : undefined;

    const baseClassName = cn(
      styles.root,
      glitch && "group/glitch isolate",
      "rounded-card r-card-lg p-[var(--space-4)] border border-card-hairline/60 bg-card/60 text-card-foreground",
      depthShadowClasses[depth],
      className,
    );

    const overlayEnabled = glitch && !hasCardOverlay(children);
    const glitchOverlay = overlayEnabled ? (
      <span
        aria-hidden
        data-card-overlay="true"
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[inherit]"
      >
        <span
          className="absolute inset-0 rounded-[inherit] bg-gradient-blob-primary opacity-0 blur-[var(--space-4)] transition-opacity duration-motion-sm ease-out motion-reduce:animate-none motion-safe:animate-blob-drift group-hover/glitch:opacity-[var(--glitch-overlay-opacity-card,0.08)] group-focus-visible/glitch:opacity-[var(--glitch-overlay-opacity-card,0.08)] group-focus-within/glitch:opacity-[var(--glitch-overlay-opacity-card,0.08)]"
        />
        <span
          className="absolute inset-0 rounded-[inherit] bg-glitch-noise bg-cover opacity-0 mix-blend-screen transition-opacity duration-motion-sm ease-out motion-reduce:animate-none motion-safe:animate-glitch-noise group-hover/glitch:opacity-[var(--glitch-noise-level,0.06)] group-focus-visible/glitch:opacity-[var(--glitch-noise-level,0.06)] group-focus-within/glitch:opacity-[calc(var(--glitch-noise-level,0.06)*1.3)]"
        />
      </span>
    ) : null;

    const glitchSprite = (
      <div aria-hidden className="glitch-sprite rounded-[inherit]" />
    );

    const baseProps = {
      className: baseClassName,
      "data-depth": depth,
      "data-glitch": glitch ? "true" : undefined,
      "data-text": glitchText,
      ...restProps,
    };

    if (asChild) {
      const childCount = React.Children.count(children);
      if (childCount !== 1 || !React.isValidElement(children)) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[Card] `asChild` requires a single valid React element child.");
        }

        return null;
      }

      const child = React.Children.only(children) as React.ReactElement<{ children?: React.ReactNode }>;
      const content = (
        <>
          {glitchSprite}
          {glitchOverlay}
          {child.props.children}
          {overlay}
        </>
      );

      return (
        <Slot {...baseProps} ref={ref as React.ForwardedRef<HTMLElement>}>
          {React.cloneElement(child, undefined, content)}
        </Slot>
      );
    }

    return (
      <div ref={ref} {...baseProps}>
        {glitchSprite}
        {glitchOverlay}
        {children}
        {overlay}
      </div>
    );
  },
);
Card.displayName = "Card";

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col space-y-[var(--space-2)]",
      className,
    )}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn("text-title font-semibold tracking-[-0.01em]", className)}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-ui text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("pt-[var(--space-2)]", className)}
    {...props}
  />
));
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center pt-[var(--space-4)]", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";
const CardBody = CardContent;

export { Card, CardBody };
